'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import {
  Loader2,
  PauseCircle,
  FileText,
  CheckCircle,
  XCircle,
  History,
  Layers,
  SkipForward
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { useHashDialog } from '@/hooks/use-hash-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from '@/i18n/routing';
import { formatDuration } from '@/lib/format-duration';
import { TemplateFieldsInfo } from '@/features/ocr-templates';
import { ocrQueries } from '../services/ocr.queries';
import { OCR_HASH } from '../constants';
import { StartOcrButton } from './start-ocr-button';
import { OcrHistorySheet } from './ocr-history-sheet';
import { displayOcrStatus, type DisplayOcrStatus } from '../utils/run-status';
import type { OcrResult } from '../types';

// No `pending` arm: `displayOcrStatus` folds it into `processing`, matching
// the listing's collapsed `ocr_status`.
const STATUS_CONFIG: Record<
  DisplayOcrStatus,
  { icon: typeof CheckCircle; label: string; className: string }
> = {
  processing: {
    icon: Loader2,
    label: 'Processing',
    className: 'border-blue-500/30 bg-blue-500/10 text-blue-600'
  },
  paused: {
    icon: PauseCircle,
    label: 'Paused',
    className: 'border-amber-500/30 bg-amber-500/10 text-amber-600'
  },
  completed: {
    icon: CheckCircle,
    label: 'Completed',
    className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600'
  },
  completed_with_errors: {
    icon: XCircle,
    label: 'Completed with errors',
    className: 'border-amber-500/30 bg-amber-500/10 text-amber-600'
  },
  failed: {
    icon: XCircle,
    label: 'Failed',
    className: 'border-red-500/30 bg-red-500/10 text-red-600'
  },
  skipped: {
    icon: SkipForward,
    label: 'Skipped',
    className: 'border-muted-foreground/30 bg-muted/10 text-muted-foreground'
  }
};

interface ContainerOcrPanelProps {
  containerId: number;
  /**
   * Registration the container belongs to. When provided, the start button
   * opens the multi-template pool dialog so the operator can pick which
   * candidate templates to run. Without it, the legacy single-template flow
   * stays in effect.
   */
  registrationId?: number;
}

export function ContainerOcrPanel({
  containerId,
  registrationId
}: ContainerOcrPanelProps) {
  const t = useTranslations('ocr');
  // Previous-results history sheet open-state lives in the URL hash (#history)
  // so it is deep-linkable and closes on browser Back.
  const { open: historyOpen, onOpenChange: toggleHistory } = useHashDialog(
    OCR_HASH.history
  );

  const { data, isPending } = useQuery(
    ocrQueries.containerResults(containerId)
  );

  const results = useMemo(() => (data?.data ?? []) as OcrResult[], [data]);

  // Multi-template pool runs produce one sibling result per candidate
  // template, all sharing a template_pool_run_token — collapse them into a
  // single grouped entry so the list reads "one run", not N unrelated rows.
  const entries = useMemo(() => {
    const grouped: Array<
      | { kind: 'single'; result: OcrResult }
      | { kind: 'pool'; token: string; siblings: OcrResult[] }
    > = [];
    const seenTokens = new Set<string>();

    // Bucket by token in one pass rather than re-scanning `results` per row.
    // Besides dropping the O(n²), it means the token is only ever used as a
    // Map key — never compared with `===` in a loop, which is what made a
    // plain run-correlation id read to the scanner as a secret compared in
    // non-constant time (CWE-208).
    const byToken = new Map<string, OcrResult[]>();
    for (const result of results) {
      const token = result.template_pool_run_token;
      if (!token) continue;
      const bucket = byToken.get(token);
      if (bucket) bucket.push(result);
      else byToken.set(token, [result]);
    }

    for (const result of results) {
      const token = result.template_pool_run_token;
      if (token) {
        const siblings = byToken.get(token) ?? [];
        if (siblings.length >= 2) {
          if (seenTokens.has(token)) continue;
          seenTokens.add(token);
          grouped.push({ kind: 'pool', token, siblings });
          continue;
        }
      }
      grouped.push({ kind: 'single', result });
    }

    return grouped;
  }, [results]);

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <h3 className='text-lg font-semibold'>{t('ocrResults')}</h3>
        <div className='flex items-center gap-2'>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={() => toggleHistory(true)}
          >
            <History className='me-2 h-4 w-4' />
            {t('previousResults')}
          </Button>
          <StartOcrButton
            containerId={containerId}
            registrationId={registrationId}
          />
        </div>
      </div>

      <OcrHistorySheet
        open={historyOpen}
        onOpenChange={toggleHistory}
        containerId={containerId}
      />

      {isPending ? (
        <div className='flex items-center justify-center py-8'>
          <Loader2 className='text-muted-foreground h-6 w-6 animate-spin' />
        </div>
      ) : results.length === 0 ? (
        <div className='flex flex-col items-center justify-center rounded-lg border border-dashed py-8'>
          <FileText className='text-muted-foreground mb-3 h-8 w-8' />
          <p className='text-muted-foreground text-sm'>{t('noResults')}</p>
          <p className='text-muted-foreground mt-1 text-xs'>
            {t('noResultsHint')}
          </p>
        </div>
      ) : (
        <div className='space-y-2'>
          {entries.map((entry) => {
            if (entry.kind === 'pool') {
              const latest = entry.siblings.reduce((a, b) =>
                b.id > a.id ? b : a
              );
              return (
                <Link
                  key={entry.token}
                  href={`/dashboard/ocr-containers/${latest.id}`}
                  className='hover:bg-muted/50 flex items-start gap-3 rounded-lg border p-3 transition-colors'
                >
                  <Layers className='text-primary h-5 w-5 shrink-0' />
                  <div className='min-w-0 flex-1'>
                    <div className='flex items-center gap-2'>
                      <span className='text-sm font-medium'>
                        {t('templatePool')}
                      </span>
                      <Badge variant='outline' className='text-[10px]'>
                        {entry.siblings.length}
                      </Badge>
                    </div>
                    <div className='mt-1 flex flex-wrap gap-1'>
                      {entry.siblings.map((sibling) => (
                        <Badge
                          key={sibling.id}
                          variant='outline'
                          className={cn(
                            'flex items-center gap-1 text-[10px]',
                            STATUS_CONFIG[displayOcrStatus(sibling.status)]
                              .className
                          )}
                        >
                          <span className='truncate'>
                            {sibling.ocr_template?.name ?? `#${sibling.id}`}
                          </span>
                          <TemplateFieldsInfo
                            templateId={sibling.ocr_template_id ?? null}
                          />
                        </Badge>
                      ))}
                    </div>
                  </div>
                </Link>
              );
            }

            const result = entry.result;
            const resultStatus = displayOcrStatus(result.status);
            const statusConfig = STATUS_CONFIG[resultStatus];
            const StatusIcon = statusConfig.icon;
            const fieldCount = result.extracted_data?.length ?? 0;

            return (
              <Link
                key={result.id}
                href={`/dashboard/ocr-containers/${result.id}`}
                className='hover:bg-muted/50 flex items-center gap-3 rounded-lg border p-3 transition-colors'
              >
                <StatusIcon
                  className={cn(
                    'h-5 w-5 shrink-0',
                    resultStatus === 'processing' && 'animate-spin',
                    statusConfig.className.split(' ').pop()
                  )}
                />
                <div className='min-w-0 flex-1'>
                  <div className='flex items-center gap-2'>
                    <span className='text-sm font-medium'>
                      {result.page_number ??
                        `Page ${result.batch_page_record_id}`}
                    </span>
                    <Badge
                      variant='outline'
                      className={cn('text-[10px]', statusConfig.className)}
                    >
                      {statusConfig.label}
                    </Badge>
                  </div>
                  {result.status === 'completed' && (
                    <p className='text-muted-foreground text-xs'>
                      {fieldCount} {t('fieldsExtracted')}
                      {result.duration_ms != null &&
                        ` · ${formatDuration(result.duration_ms)}`}
                    </p>
                  )}
                  {result.status === 'failed' && result.error_message && (
                    <p className='text-destructive truncate text-xs'>
                      {result.error_message}
                    </p>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
